import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import BaseModal from '@components/common/form/BaseModal';
import apiConfig from '@constants/apiConfig';
import { errorCodes } from '@constants/ErrorCode';
import useFetch from '@hooks/useFetch';
import useNotification from '@hooks/useNotification';
import useTranslate from '@hooks/useTranslate';
import { commonMessage } from '@locales/intl';
import { showErrorMessage } from '@services/notifyService';

import AreaForm from './AreaForm';
import ModalFormFooter from './ModalFormFooter';

const message = defineMessages({
    objectName: 'Area',
    saveSuccess: 'Save area successfully',
});

const FORM_ID = 'form-devicetab-area';
const EMPTY = {};

const useAreaFormModal = ({ onReload, onClose } = {}) => {
    const translate = useTranslate();
    const notification = useNotification();

    const [ open, setOpen ] = useState(false);
    const [ detail, setDetail ] = useState(null);
    const [ isEditing, setIsEditing ] = useState(false);
    const [ changed, setChanged ] = useState(false);
    const [ ordering, setOrdering ] = useState(undefined);

    const { execute: executeGetDetail } = useFetch(apiConfig.areas.getById, { immediate: false });
    const { execute: executeCreate, loading: loadingCreate } = useFetch(apiConfig.areas.create, { immediate: false });
    const { execute: executeUpdate, loading: loadingUpdate } = useFetch(apiConfig.areas.update, { immediate: false });

    const handleSaveError = (err) => {
        const errorCode = err?.response?.data?.code ?? err?.code;
        const errorMessage = errorCodes[errorCode]?.message;
        if (errorMessage) showErrorMessage(translate.formatMessage(errorMessage), translate);
        else showErrorMessage(err?.message || translate.formatMessage(commonMessage.error), translate);
    };

    const closeModal = () => {
        setOpen(false);
        setDetail(null);
        setChanged(false);
        setOrdering(undefined);
        onClose?.();
    };

    const openAreaCreate = (nextOrdering) => {
        setIsEditing(false);
        setDetail({});
        setChanged(false);
        setOrdering(nextOrdering);
        setOpen(true);
    };

    const openAreaEdit = (record) => {
        executeGetDetail({
            pathParams: { id: record.id },
            onCompleted: (res) => {
                setDetail(res?.result === true ? res.data : {});
                setIsEditing(true);
                setChanged(false);
                setOpen(true);
            },
            onError: () => notification({ type: 'error', message: translate.formatMessage(commonMessage.error) }),
        });
    };

    const onSuccess = () => {
        notification({ message: translate.formatMessage(message.saveSuccess) });
        closeModal();
        onReload?.();
    };

    const handleSubmit = (values) => {
        if (isEditing) {
            executeUpdate({
                data: { ...values, id: detail?.id },
                onCompleted: (res) => res?.result === true && onSuccess(),
                onError: handleSaveError,
            });
        } else {
            executeCreate({
                data: { ...values, ordering: ordering != null ? Number(ordering) : undefined },
                onCompleted: (res) => res?.result === true && onSuccess(),
                onError: handleSaveError,
            });
        }
    };

    const renderModals = () =>
        (
            <BaseModal
                isModalVisible={open}
                isEditing={isEditing}
                handleCancel={closeModal}
                title={translate.formatMessage(message.objectName)}
                width="500px"
                noBodyPadding={true}
                customFooter={
                    <ModalFormFooter
                        formId={FORM_ID}
                        onCancel={closeModal}
                        loading={loadingCreate || loadingUpdate}
                        disabled={!changed}
                        isEditing={isEditing}
                    />
                }
            > {open &&  (
                    <AreaForm
                        formId={FORM_ID}
                        dataDetail={detail || EMPTY}
                        isEditing={isEditing}
                        actions={null}
                        onSubmit={handleSubmit}
                        setIsChangedFormValues={setChanged}
                    />
                )}
            </BaseModal>
        );

    return { openAreaCreate, openAreaEdit, renderModals };
};

export default useAreaFormModal;
